<template>
  <EditorNodeWrapper
    :class="[
      'node-container',
      'video-container',
      blockWidthClass,
      node.align.toLowerCase(),
      node.imageSize.toLowerCase(),
    ]"
    @observe-node-visibility="$emit('observe-node-visibility', { ...$event, nodeId: node.id })"
  >
    <VideoPlayer
      :url="node.url"
      :thumb-url="node.srcUrl || videoThumbUrl"
      :ui-styling="uiStyling"
      ratio="16:9"
      class="video-player viewer"
      @playing="handlePlaying"
      @pause="handlePause"
      @ended="handleEnded"
    />
  </EditorNodeWrapper>
</template>
<script setup lang="ts">
import { useLoadedVideosStore, type EBlockWidth, type Video } from '@getaccept/editor-lib';
import VideoPlayer from '@getaccept/lib-shared/src/video/player/components/VideoPlayer.vue';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useTimer } from '@getaccept/lib-shared/src/composables/timer.composable';
import { useInjectEditorViewState } from '../composables/editor-view-state.composable';
import EditorNodeWrapper from './EditorNodeWrapper.vue';

const { node } = defineProps<{
  node: Video;
  blockWidthClass?: EBlockWidth;
}>();

const emit = defineEmits<{
  'observe-node-visibility': [{ visible: boolean; nodeId: string }];
  'track-video': [{ nodeId: string; videoDuration: number }];
}>();

const { startTimer, stopTimer, elapsedTime } = useTimer();

const handlePlaying = () => {
  startTimer();
};

const sendTrackingData = () => {
  if (elapsedTime.value > 0) {
    emit('track-video', {
      nodeId: node.id,
      videoDuration: elapsedTime.value,
    });
    elapsedTime.value = 0;
  }
};

const handlePause = () => {
  stopTimer();
  sendTrackingData();
};

const handleEnded = () => {
  stopTimer();
  sendTrackingData();
};

const loadedVideosStore = useLoadedVideosStore();
const { videos } = storeToRefs(loadedVideosStore);

const videoThumbUrl = computed(
  () => videos.value.find(video => video.id === node.videoId)?.thumbUrl
);

const { uiStyling } = useInjectEditorViewState();
</script>
<style scoped>
@import '@getaccept/editor-lib/src/css/editor-node-video.css';
</style>
